/**
 * StatusFooter — cockpit-lovable-alignment Phase 4 Session 4.4 (2026-05-15).
 *
 * Root-level honest-static-stubs footer (28px / `h-7`) mounted by `__root.tsx`
 * inside the post-cascade authed-render branch — sibling AFTER <main> INSIDE
 * the `<div className="flex-1 flex flex-col min-w-0 overflow-hidden">`
 * wrapper.
 *
 * Lovable Ref #4 content + typography port (line 248-289 verbatim shape →
 * Tailwind token translation). Dynamic values are STATIC LITERALS per spec
 * §7 honest-stubs invariant — no setTimeout/setInterval/animation in any
 * value. Only the accent dot animates (CSS `animate-pulse-status` keyframe at
 * `index.css:224`, reduced-motion-guarded at `index.css:315`).
 *
 * Self-gates on `useAuth().user` as defense-in-depth — `__root.tsx`'s
 * post-cascade render branch already filters non-authed before this code
 * path renders, but the spec §7-required guard makes the component safe to
 * import elsewhere.
 *
 * Z-stack: z-20 sits BELOW NarrativeBar z-30 on Builder/Solver/`/deal/*`
 * (correct — NarrativeBar's expanded overlay needs above-chrome stacking);
 * zero collision on Cockpit/Pipeline/Settings/Admin (NarrativeBar not mounted
 * there).
 */

import { useAuth } from '@/hooks/use-auth';
import { SupportLink } from '@/components/ui';

const REGION =
  (import.meta.env.VITE_REGION as string | undefined) ?? 'us-east-1';

const itemClass =
  'text-[8px] font-mono uppercase tracking-[0.5px] text-text-dim';
const sepClass = 'text-[8px] font-mono text-text-dim';

export function StatusFooter() {
  const { user } = useAuth();
  if (!user) return null;

  return (
    <div
      data-testid="status-footer"
      className="flex items-center justify-between px-4 h-7 bg-void border-t border-panel-border z-20 shrink-0"
    >
      <div className="flex items-center gap-2">
        <span
          data-testid="status-footer-accent-dot"
          className="w-1 h-1 rounded-full bg-accent animate-pulse-status"
          aria-hidden="true"
        />
        <span className="text-[8px] font-mono uppercase tracking-[0.5px] text-text-bright">
          SYSTEM ONLINE
        </span>
        <span className={sepClass}>·</span>
        <span className={itemClass}>LATENCY: 12ms</span>
        <span className={sepClass}>·</span>
        <span className={itemClass}>6 FEEDS ACTIVE</span>
        <span className={sepClass}>·</span>
        <span className={itemClass}>QUEUE: 0</span>
        <span className={sepClass}>·</span>
        <span className={itemClass}>DB SYNC: &lt;1s</span>
        <span className={sepClass}>·</span>
        <span className={itemClass}>CACHE: warm</span>
      </div>
      <div className="flex items-center gap-2">
        {/* Persistent always-on support hook for the authed app. Terminal
            chrome (LABEL: value rhythm) matches REGION. Sits BEFORE REGION
            so the support address is the first thing in the right cluster. */}
        <SupportLink variant="footer" />
        <span className={sepClass}>·</span>
        <span data-testid="status-footer-region" className={itemClass}>
          REGION: {REGION}
        </span>
      </div>
    </div>
  );
}
